import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";

// 定义ChartStyle接口代替导入
interface ChartStyle {
  id: string | number;
  name: string;
  titleFont: string;
  titleSize: string;
  coupleNamesFont: string;
  coupleNamesSize: string;
  dateFont: string;
  dateSize: string;
  tableFont: string;
  tableFontSize: string;
  guestListFont: string;
  guestListFontSize: string;
}

interface FontSelectorProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

export const FontSelector: React.FC<FontSelectorProps> = ({ value, onChange, options }) => {
  // 添加一个状态来管理下拉列表是否打开
  const [isOpen, setIsOpen] = React.useState(false);
  const selectorRef = React.useRef<HTMLDivElement>(null);
  
  // 点击外部时关闭下拉列表
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <div className="font-selector" ref={selectorRef}>
      {/* 当前选中项 */}
      <div 
        className="font-selector-selected" 
        onClick={() => setIsOpen(!isOpen)}
        style={{ fontFamily: value }}
      >
        <span className="font-preview">{value}</span>
        <span className="dropdown-arrow">▼</span>
      </div>
      
      {/* 下拉选项列表 */}
      {isOpen && (
        <div className="font-selector-options">
          {options.map((option) => (
            <div
              key={option.value}
              className={`font-option ${value === option.value ? 'selected' : ''}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{ fontFamily: option.value }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface FontSizeSelectorProps {
  element: string;
  value: string;
  onChange: (element: string, value: string) => void;
}

const FontSizeSelector: React.FC<FontSizeSelectorProps> = ({
  element,
  value,
  onChange,
}) => {
  const size = parseInt(value.replace("px", ""));

  return (
    <div className="size-input-container">
      <input
        type="number"
        className="size-input"
        value={size}
        onChange={(e) => {
          const newSize = e.target.value;
          onChange(element, `${newSize}px`);
        }}
        min={8}
        max={72}
      />
      <span className="unit">px</span>
    </div>
  );
};

interface StyleSidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedStyle: ChartStyle;
  setSelectedStyle: (style: ChartStyle) => void;
  showTables: boolean;
  setShowTables: (show: boolean) => void;
  fontOptions: Array<{ value: string; label: string }>;
  defaultStyles: ChartStyle[];
  resetTableOrder?: () => void;
  resetBackground?: () => void;
  saveCurrentState?: () => void;
  handleStyleSelect?: (style: ChartStyle) => void;
  showAllTableCards: () => void;
}

const StyleSidebar: React.FC<StyleSidebarProps> = ({
  isOpen,
  setIsOpen,
  selectedStyle,
  setSelectedStyle,
  showTables,
  setShowTables,
  fontOptions,
  defaultStyles,
  resetTableOrder,
  resetBackground,
  saveCurrentState,
  handleStyleSelect,
  showAllTableCards,
}) => {
  // 添加状态来控制确认对话框
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // 添加useEffect用于样式注入
  React.useEffect(() => {
    // 添加字体组和输入相关样式
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .font-group {
        margin-bottom: 20px;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }
      
      .font-select-container, .size-select-container {
        display: flex;
        flex-direction: column;
      }
      
      .font-select-container {
        width: 60%;
      }
      
      .size-select-container {
        width: 30%;
      }
      
      .font-select-container label, .size-select-container label {
        margin-bottom: 5px;
        white-space: nowrap;
        text-align: left;
      }
      
      .font-select-container select {
        width: 100%;
        padding: 5px;
      }
      
      .size-input-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }
      
      .size-input {
        width: 50px;
        padding: 4px;
        border: 1px solid #ccc;
        border-radius: 4px;
      }
      
      .unit {
        margin-left: 5px;
        color: #666;
      }
    `;
    document.head.appendChild(styleElement);

    // 添加字体选择器相关样式
    const fontSelectorStyles = document.createElement('style');
    fontSelectorStyles.textContent = `
      .font-selector {
        position: relative;
        width: 100%;
      }
      
      .font-selector-selected {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 10px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background-color: white;
        cursor: pointer;
        height: 30px;
        overflow: hidden;
      }
      
      .dropdown-arrow {
        font-size: 10px;
        color: #666;
      }
      
      .font-selector-options {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        max-height: 250px;
        overflow-y: auto;
        background-color: white;
        border: 1px solid #ccc;
        border-top: none;
        border-radius: 0 0 4px 4px;
        z-index: 100;
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
      }
      
      .font-option {
        padding: 8px 10px;
        cursor: pointer;
        display: flex;
        align-items: center;
      }
      
      .font-option:hover {
        background-color: #f5f5f5;
      }
      
      .font-option.selected {
        background-color: #e0e0e0;
      }
      
      .font-preview {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      /* 为下拉选择器中的每个字体选项添加预览样式 */
      select option[value="Dancing Script"] {
        font-family: "Dancing Script", cursive;
        font-size: 16px;
        padding: 10px;
      }
      
      select option[value="Playfair Display"] {
        font-family: "Playfair Display", serif;
        font-size: 16px;
        padding: 10px;
      }
      
      select option[value="Lora"] {
        font-family: "Lora", serif;
        font-size: 16px;
        padding: 10px;
      }
      
      select option[value="Pacifico"] {
        font-family: "Pacifico", cursive;
        font-size: 16px;
        padding: 10px;
      }
      
      select option[value="Satisfy"] {
        font-family: "Satisfy", cursive;
        font-size: 16px;
        padding: 10px;
      }
      
      select option[value="Mea Culpa"] {
        font-family: "Mea Culpa", cursive;
        font-size: 16px;
        padding: 10px;
      }
      
      select option[value="Fleur De Leah"] {
        font-family: "Fleur De Leah", cursive;
        font-size: 16px;
        padding: 10px;
      }
      
      /* 为选择器本身设置样式 */
      .font-select-container select {
        width: 100%;
        padding: 8px;
        border: 1px solid #ccc;
        border-radius: 4px;
        appearance: menulist;
        font-size: 14px;
      }
      
      /* 让选中项显示对应字体 */
      .font-select-container select,
      .font-select-container select option:checked {
        font-family: inherit;
      }
    `;
    document.head.appendChild(fontSelectorStyles);

    // 添加超宽模态框样式
    const extraWideModalStyle = document.createElement('style');
    extraWideModalStyle.textContent = `
      .extra-wide-modal {
        max-width: 1000px;
        width: 95%;
        margin: 1.75rem auto;
      }
    `;
    document.head.appendChild(extraWideModalStyle);

    // 添加侧边栏滚动样式
    const sidebarScrollStyle = document.createElement('style');
    sidebarScrollStyle.textContent = `
      .style-sidebar {
        display: flex;
        flex-direction: column;
        max-height: 100vh;
        overflow-y: auto;
      }
      
      /* 美化滚动条 */
      .style-sidebar::-webkit-scrollbar {
        width: 8px;
      }
      
      .style-sidebar::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      
      .style-sidebar::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 4px;
      }
      
      .style-sidebar::-webkit-scrollbar-thumb:hover {
        background: #aaa;
      }
      
      /* 确保头部固定 */
      .style-sidebar-header {
        position: sticky;
        top: 0;
        background-color: white;
        z-index: 10;
        padding-bottom: 10px;
      }
    `;
    document.head.appendChild(sidebarScrollStyle);

    // 在组件卸载时移除样式元素
    return () => {
      document.head.removeChild(styleElement);
      document.head.removeChild(fontSelectorStyles);
      document.head.removeChild(extraWideModalStyle);
      document.head.removeChild(sidebarScrollStyle);
    };
  }, []);

  const handleFontChange = (element: string, value: string) => {
    if (element === "title") {
      setSelectedStyle({ ...selectedStyle, titleFont: value });
    } else if (element === "couple-names") {
      setSelectedStyle({ ...selectedStyle, coupleNamesFont: value });
    } else if (element === "wedding-date") {
      setSelectedStyle({ ...selectedStyle, dateFont: value });
    } else if (element === "table-name") {
      setSelectedStyle({ ...selectedStyle, tableFont: value });
    } else if (element === "guest-list") {
      setSelectedStyle({ ...selectedStyle, guestListFont: value });
    }
  };

  const handleFontSizeChange = (element: string, value: string) => {
    if (element === "title") {
      setSelectedStyle({ ...selectedStyle, titleSize: value });
    } else if (element === "couple-names") {
      setSelectedStyle({ ...selectedStyle, coupleNamesSize: value });
    } else if (element === "wedding-date") {
      setSelectedStyle({ ...selectedStyle, dateSize: value });
    } else if (element === "table-name") {
      setSelectedStyle({ ...selectedStyle, tableFontSize: value });
    } else if (element === "guest-list") {
      setSelectedStyle({ ...selectedStyle, guestListFontSize: value });
    }
  };

  // 修改重置样式的处理函数流程
  const showResetStyleConfirm = () => {
    setShowResetConfirm(true); // 显示确认对话框
  };

  const confirmResetStyle = () => {
    setShowResetConfirm(false); // 关闭确认对话框
    
    // 在重置前保存当前状态
    if (saveCurrentState) {
      saveCurrentState();
    }
    
    // 找到当前样式ID对应的默认样式
    const defaultStyle = defaultStyles.find(style => style.id === selectedStyle.id);
    if (defaultStyle) {
      setSelectedStyle(defaultStyle);
    }
    
    // 如果提供了resetTableOrder回调，则调用它
    if (resetTableOrder) {
      resetTableOrder();
    }
    
    // 如果提供了resetBackground回调，则调用它
    if (resetBackground) {
      resetBackground();
    }
  };

  return (
    <div className={`style-sidebar ${isOpen ? "open" : ""}`}>
      <div className="style-sidebar-header">
        <h3>编辑样式</h3>
        <button
          className="style-sidebar-close"
          onClick={() => setIsOpen(false)}
        >
          ×
        </button>
      </div>

      <div className="style-section">
        <h4 className="section-title">预设样式</h4>
        <div className="style-options">
          {defaultStyles.map((style) => (
            <div
              key={style.id}
              className={`style-option ${selectedStyle.id === style.id ? "selected" : ""}`}
              onClick={() => handleStyleSelect && handleStyleSelect(style)}
            >
              <h4>{style.name}</h4>
            </div>
          ))}
        </div>
      </div>
      
      {/* 添加恢复初始化设置按钮 */}
      <div className="style-section">
        <Button
          variant="outline-secondary"
          onClick={showResetStyleConfirm}
          className="custom-style-btn"
          style={{ width: '100%', marginBottom: '15px' }}
        >
          重置样式
        </Button>
      </div>

      {/* 添加美化后的确认对话框 */}
      <Modal 
        show={showResetConfirm} 
        onHide={() => setShowResetConfirm(false)}
        centered
        className="reset-style-modal"
        size="xl"
        dialogClassName="extra-wide-modal"
      >
        <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <Modal.Title style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#d9534f' }}>
            <i className="fa fa-exclamation-triangle" style={{ marginRight: '10px' }}></i>
            Confirm Reset Style
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px', fontSize: '1.1rem' }}>
          <div style={{ marginBottom: '15px' }}>
            <p>This action will reset the following settings:</p>
            <ul style={{ paddingLeft: '25px', marginTop: '10px', color: '#555' }}>
              <li>All fonts and font sizes</li>
              <li>Table arrangement order</li>
              <li>Background image</li>
            </ul>
          </div>
          <div style={{ 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeeba', 
            borderRadius: '5px',
            padding: '12px 15px',
            marginTop: '10px',
            width: '100%',
            overflowX: 'visible'
          }}>
            <p style={{ 
              color: '#856404', 
              margin: 0, 
              display: 'flex', 
              alignItems: 'center'
            }}>
              <i className="fa fa-info-circle" style={{ marginRight: '10px', fontSize: '1.2rem', flexShrink: 0 }}></i>
              <span><strong>Note:</strong> This action <strong>cannot be undone</strong></span>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none', paddingTop: 0 }}>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowResetConfirm(false)}
            style={{ padding: '8px 20px', fontWeight: '500' }}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmResetStyle}
            style={{ padding: '8px 20px', fontWeight: '500' }}
          >
            Confirm Reset
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="style-section">
        <h4 className="section-title">字体设置</h4>
        <div className="font-options">
          <div className="font-group">
            <div className="font-select-container">
              <label>标题字体</label>
              <FontSelector
                value={selectedStyle.titleFont}
                onChange={(value) => handleFontChange("title", value)}
                options={fontOptions}
              />
            </div>
            <div className="size-select-container">
              <label>字号</label>
              <FontSizeSelector
                element="title"
                value={selectedStyle.titleSize}
                onChange={handleFontSizeChange}
              />
            </div>
          </div>

          <div className="font-group">
            <div className="font-select-container">
              <label>新郎新娘名字字体</label>
              <FontSelector
                value={selectedStyle.coupleNamesFont}
                onChange={(value) => handleFontChange("couple-names", value)}
                options={fontOptions}
              />
            </div>
            <div className="size-select-container">
              <label>字号</label>
              <FontSizeSelector
                element="couple-names"
                value={selectedStyle.coupleNamesSize}
                onChange={handleFontSizeChange}
              />
            </div>
          </div>

          <div className="font-group">
            <div className="font-select-container">
              <label>日期字体</label>
              <FontSelector
                value={selectedStyle.dateFont}
                onChange={(value) => handleFontChange("wedding-date", value)}
                options={fontOptions}
              />
            </div>
            <div className="size-select-container">
              <label>字号</label>
              <FontSizeSelector
                element="wedding-date"
                value={selectedStyle.dateSize}
                onChange={handleFontSizeChange}
              />
            </div>
          </div>

          <div className="font-group">
            <div className="font-select-container">
              <label>桌子名称字体</label>
              <FontSelector
                value={selectedStyle.tableFont}
                onChange={(value) => handleFontChange("table-name", value)}
                options={fontOptions}
              />
            </div>
            <div className="size-select-container">
              <label>字号</label>
              <FontSizeSelector
                element="table-name"
                value={selectedStyle.tableFontSize}
                onChange={handleFontSizeChange}
              />
            </div>
          </div>

          <div className="font-group">
            <div className="font-select-container">
              <label>宾客列表字体</label>
              <FontSelector
                value={selectedStyle.guestListFont}
                onChange={(value) => handleFontChange("guest-list", value)}
                options={fontOptions}
              />
            </div>
            <div className="size-select-container">
              <label>字号</label>
              <FontSizeSelector
                element="guest-list"
                value={selectedStyle.guestListFontSize}
                onChange={handleFontSizeChange}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="style-section">
        <h4 className="section-title">自定义设置</h4>
        <div className="style-options">
          {selectedStyle.id === 1 && (
            <Button
              variant="outline-secondary"
              onClick={() => setShowTables(!showTables)}
              className="custom-style-btn"
              style={{ width: '100%', marginBottom: '15px' }}
            >
              {showTables ? "隐藏桌位图" : "显示桌位图"}
            </Button>
          )}
          
          {/* 在自定义设置中添加显示所有桌子纸片按钮 */}
          {selectedStyle.id === 2 && (
            <Button
              variant="outline-secondary"
              onClick={showAllTableCards}
              className="custom-style-btn"
              style={{ width: '100%', marginBottom: '15px' }}
            >
              显示所有桌子纸片
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StyleSidebar; 